import { useState, useEffect } from "react";
import { Box, Container, Button, IconButton, Drawer, Link } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

const NAV = [
  { label: "Platform",     id: "features"     },
  { label: "How It Works", id: "how-it-works" },
  { label: "Industries",   id: "industries"   },
  { label: "Why Us",       id: "why-us"       },
];

const GOLD      = "#D97706";
const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";

export default function Header({ onRequestDemo }) {
  const [scrolled,   setScrolled]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setDrawerOpen(false);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
          py: scrolled ? 1 : 1.5,
          background: scrolled ? "rgba(255,255,255,0.95)" : "#ffffff",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: scrolled ? "0 1px 12px rgba(15,23,42,.08)" : "none",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", alignItems: "center", height: 56 }}>

            {/* Logo */}
            <Link href="/" underline="none" sx={{ display: "flex", alignItems: "center", mr: "auto" }}>
              <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 40, display: "block" }} />
            </Link>

            {/* Desktop nav */}
            <Box component="nav" sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5 }}>
              {NAV.map(n => (
                <Box
                  key={n.id}
                  component="button"
                  onClick={() => go(n.id)}
                  aria-label={`Navigate to ${n.label}`}
                  sx={{
                    background: "none", border: "none", cursor: "pointer",
                    px: 2.5, py: 1.1, borderRadius: "8px",
                    fontSize: 14, fontWeight: 500, color: "#475569",
                    fontFamily: "IBM Plex Sans, sans-serif",
                    transition: "all .18s",
                    "&:hover": { color: "#0F172A", background: "#F1F5F9" },
                  }}
                >
                  {n.label}
                </Box>
              ))}

              <Button
                href="/mctrl/login"
                sx={{
                  ml: 2, px: 2.5, py: 1, borderRadius: "9px",
                  fontSize: 14, fontWeight: 500, textTransform: "none",
                  color: "#475569", border: "1px solid #CBD5E1",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  "&:hover": { border: "1px solid #94A3B8", background: "#F8FAFC", color: "#0F172A" },
                  transition: "all .2s",
                }}
              >
                Sign In
              </Button>

              <Button
                onClick={onRequestDemo}
                sx={{
                  ml: 1, px: 3, py: 1, borderRadius: "9px",
                  fontSize: 14, fontWeight: 600, textTransform: "none",
                  background: GOLD_GRAD, color: "#fff",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  boxShadow: "0 4px 14px rgba(217,119,6,.3)",
                  cursor: "pointer",
                  "&:hover": {
                    background: "linear-gradient(135deg,#B45309,#D97706)",
                    boxShadow: "0 6px 20px rgba(217,119,6,.45)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all .2s",
                }}
              >
                Request Demo
              </Button>
            </Box>

            <IconButton
              aria-label="Open menu"
              sx={{ display: { xs: "flex", md: "none" }, color: "#0F172A", ml: "auto" }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, background: "#fff", borderLeft: "1px solid #E2E8F0" } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 5 }}>
            <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 34, display: "block" }} />
            <IconButton aria-label="Close menu" sx={{ color: "#64748B" }} onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {NAV.map(n => (
              <Box
                key={n.id}
                component="button"
                onClick={() => go(n.id)}
                sx={{
                  background: "none", border: "none", cursor: "pointer", textAlign: "left",
                  px: 2, py: 1.5, borderRadius: "10px",
                  fontSize: 15, fontWeight: 500, color: "#475569",
                  fontFamily: "IBM Plex Sans, sans-serif",
                  transition: "all .15s",
                  "&:hover": { background: "#F1F5F9", color: "#0F172A" },
                }}
              >
                {n.label}
              </Box>
            ))}
            <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button href="/mctrl/login" fullWidth sx={{ border: "1px solid #CBD5E1", color: "#475569", textTransform: "none", fontWeight: 500, borderRadius: "10px", py: 1.3, fontFamily: "IBM Plex Sans, sans-serif", "&:hover": { background: "#F8FAFC" } }}>Sign In</Button>
              <Button onClick={onRequestDemo} fullWidth sx={{ background: GOLD_GRAD, color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: "10px", py: 1.3, fontFamily: "IBM Plex Sans, sans-serif" }}>Request Demo</Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
